import React, { FC, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../../_metronic/helpers'
import { getPmsJobHistory } from '../../core/pms/_requests'
import type { PmsJobEventDto, PmsJobRecord } from '../../core/pms/_models'
import { PrintJobHistoryModal } from './PrintJobHistoryModal'
import { formatJobDate, getJobDueReasonLabel } from './pmsJobHelpers'

interface Props {
  visible: boolean
  job: PmsJobRecord | null
  historyRecords?: PmsJobRecord[]
  onClose: () => void
}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const LABEL = 'form-label fw-semibold fs-6 mb-1 text-dark'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const PmsJobHistoryModal: FC<Props> = ({ visible, job, historyRecords, onClose }) => {
  const [events, setEvents] = useState<PmsJobEventDto[]>([])
  const [loading, setLoading] = useState(false)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<PmsJobRecord | null>(null)
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null)

  const handleSelectHistoryJob = (historyJob: PmsJobRecord) => {
    if (!historyJob.id) return
    setSelectedJob(historyJob)
    setSelectedJobId(historyJob.id)
  }

  useEffect(() => {
    const load = async () => {
      if (!selectedJobId || !visible) {
        setEvents([])
        return
      }
      setLoading(true)
      try {
        const data = await getPmsJobHistory(selectedJobId)
        setEvents(data)
      } catch (error: any) {
        toast.error('Failed to load job history', { position: 'top-center' })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [selectedJobId, visible])

  useEffect(() => {
    if (visible && job) {
      setSelectedJob(job)
      setSelectedJobId(job.id ?? null)
    } else {
      setSelectedJob(null)
      setSelectedJobId(null)
    }
  }, [job, visible])

  if (!visible || !job) return null

  const activeJob = selectedJob ?? job
  const historyList = historyRecords ?? []
  const renderedEvents = events.length > 0 ? events : []

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'CREATED': return 'text-primary'
      case 'DEFERRED': return 'text-warning'
      case 'COMPLETED': return 'text-success'
      default: return 'text-info'
    }
  }

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType) {
      case 'CREATED': return '/media/icons/duotune/general/gen005.svg'
      case 'DEFERRED': return '/media/icons/duotune/arrows/arr071.svg'
      case 'COMPLETED': return '/media/icons/duotune/general/gen048.svg'
      default: return '/media/icons/duotune/general/gen044.svg'
    }
  }

  return (
    <>
      <div
        style={WRAP_STYLE}
        tabIndex={-1}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className='bg-white text-dark rounded shadow-lg d-flex flex-column'
          style={{ width: '95vw', maxWidth: '1200px', maxHeight: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0 bg-light'>
            <div className='flex-grow-1'>
              <h5 className='modal-title text-dark fw-bold mb-1'>Job History</h5>
              <div className='text-muted fs-7'>
                <span className='fw-semibold'>{activeJob.jobCode}</span> • {activeJob.title}
              </div>
            </div>
            <div className='d-flex align-items-center gap-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => {
                    if (activeJob.id) {
                      setPdfViewerOpen(true)
                    }
                  }}
                  title='View PDF'
                >
                <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-2' />
                View PDF
              </button>
              <button type='button' className='btn-close' onClick={onClose}></button>
            </div>
          </div>

          <div className='px-4 py-3 flex-grow-1 overflow-auto'>
            {/* Job Details Card */}
            <div className='card mb-4 border'>
              <div className='card-body'>
                <h6 className='card-title text-dark fw-bold mb-3'>Job Details</h6>
                <div className='row g-3'>
                  <div className='col-md-6 col-lg-3'>
                    <div className={LABEL}>Vessel</div>
                    <div className='text-dark fw-semibold'>{activeJob.vesselName || '-'}</div>
                  </div>
                  <div className='col-md-6 col-lg-3'>
                    <div className={LABEL}>Job Code</div>
                    <div className='text-dark fw-semibold'>{activeJob.jobCode || '-'}</div>
                  </div>
                  <div className='col-md-6 col-lg-3'>
                    <div className={LABEL}>Current State</div>
                    <div>
                      <span className={`badge badge-light-${activeJob.state === 'CLOSED' ? 'success' : activeJob.state === 'DEFERRED' ? 'warning' : activeJob.state === 'CANCELLED' ? 'danger' : 'primary'}`}>
                        {activeJob.state}
                      </span>
                    </div>
                  </div>
                  <div className='col-md-6 col-lg-3'>
                    <div className={LABEL}>Due Date</div>
                    <div className='text-dark fw-semibold'>
                      {activeJob.dueDate ? new Date(activeJob.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </div>
                  </div>
                </div>
                <div className='row g-3 mt-2'>
                  <div className='col-md-4'>
                    <div className={LABEL}>Equipment</div>
                    <div className='text-dark fw-semibold'>{activeJob.equipmentName || '-'}</div>
                  </div>
                  <div className='col-md-4'>
                    <div className={LABEL}>Component</div>
                    <div className='text-dark fw-semibold'>{activeJob.subComponentName || activeJob.equipmentName || '-'}</div>
                  </div>
                  <div className='col-md-4'>
                    <div className={LABEL}>Sub-Component</div>
                    <div className='text-dark fw-semibold'>{activeJob.subComponentName || '-'}</div>
                  </div>
                </div>
                {activeJob.lastDoneDate && (
                  <div className='row g-3 mt-2'>
                    <div className='col-md-4'>
                      <div className={LABEL}>Last Done Date</div>
                      <div className='text-dark fw-semibold'>
                        {new Date(activeJob.lastDoneDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {historyList.length > 0 && (
              <div className='mb-4'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='text-dark fw-bold mb-0'>Job History Records</h6>
                  <small className='text-muted'>Latest first</small>
                </div>
                <div className='table-responsive'>
                  <table className='table table-bordered table-sm align-middle mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th>Job Code</th>
                        <th>State</th>
                        <th>Due Reason</th>
                        <th>Due Date</th>
                        <th>Due Counter</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyList.map((hist) => (
                        <tr
                          key={hist.id ?? `${hist.title}-${hist.createdAt}-${hist.jobCode}`}
                          className={selectedJob?.id === hist.id ? 'table-active' : undefined}
                        >
                          <td>
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0'
                              onClick={() => handleSelectHistoryJob(hist)}
                            >
                              {hist.jobCode || '—'}
                            </button>
                          </td>
                          <td>
                            <span className={`badge badge-light-${hist.state === 'CLOSED' ? 'success' : hist.state === 'DEFERRED' ? 'warning' : hist.state === 'CANCELLED' ? 'danger' : 'primary'}`}>
                              {hist.state}
                            </span>
                          </td>
                          <td>{getJobDueReasonLabel(hist)}</td>
                          <td>{hist.dueDate ? formatJobDate(hist.dueDate) : '-'}</td>
                          <td>{hist.dueCounter ? `${hist.dueCounter.toLocaleString()} hrs` : '-'}</td>
                          <td>{formatDateTime(hist.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* History Timeline */}
            <div>
              <h6 className='text-dark fw-bold mb-3'>History Timeline</h6>
              {loading && (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              )}
              {!loading && renderedEvents.length === 0 && (
                <div className='text-center text-muted py-5 border rounded'>
                  <KTSVG path='/media/icons/duotune/general/gen040.svg' className='svg-icon-3x text-muted mb-3' />
                  <div>No history available for this job.</div>
                </div>
              )}
              {!loading && renderedEvents.length > 0 && (
                <div className='timeline'>
                  {renderedEvents.map((ev, idx) => (
                    <div className='timeline-item' key={ev.id || `${ev.eventType}-${ev.eventTime}-${idx}`}>
                      <div className='timeline-label fw-semibold text-muted fs-7'>
                        {formatDateTime(ev.eventTime)}
                      </div>
                      <div className='timeline-badge'>
                        <KTSVG path={getEventTypeIcon(ev.eventType)} className={`svg-icon-3 ${getEventTypeColor(ev.eventType)}`} />
                      </div>
                      <div className='timeline-content'>
                        <div className='card border shadow-sm'>
                          <div className='card-body p-3'>
                            <div className='d-flex align-items-center justify-content-between mb-2'>
                              <div className={`fw-bold fs-6 ${getEventTypeColor(ev.eventType)}`}>
                                {ev.eventType}
                              </div>
                              {ev.triggeredBy && (
                                <div className='text-muted fs-7'>
                                  <i className='fas fa-user me-1'></i>
                                  {ev.triggeredBy}
                                </div>
                              )}
                            </div>
                            {ev.notes && (
                              <div className='text-dark mb-2' style={{ whiteSpace: 'pre-wrap' }}>
                                {ev.notes}
                              </div>
                            )}
                            {ev.newDueDate && (
                              <div className='text-muted fs-7'>
                                <i className='fas fa-calendar me-1'></i>
                                New due date: {formatDateTime(ev.newDueDate)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {pdfViewerOpen && activeJob.id && (
        <PrintJobHistoryModal
          visible={pdfViewerOpen}
          jobId={activeJob.id}
          jobCode={activeJob.jobCode || null}
          onClose={() => setPdfViewerOpen(false)}
        />
      )}
    </>
  )
}
